import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { jsPDF } from "jspdf";                      // ← STATIC import, no dynamic import()
import { PURCHASE_API, MASTER_API } from "../../apiConfig";
import { authHeaders } from "../../utils/authHeaders";
import sbeeLogo from "../../assets/sbeelogo.png";

// Turns "john.doe@sbee.in" or "john_doe" into "John" (first token, first letter capitalized)
const getDisplayNameFromEmail = (value) => {
  if (!value) return "";
  // A user id (no "@") is shown exactly as stored — e.g. "maithili".
  // Only real email addresses (older records) are shortened to a first name.
  if (!String(value).includes("@")) return String(value).trim();
  const namePart = String(value).split("@")[0];
  const firstToken = namePart.split(/[._\s]+/)[0] || "";
  if (!firstToken) return "";
  return firstToken.charAt(0).toUpperCase() + firstToken.slice(1).toLowerCase();
};

// Returns the currently logged-in user's display name (first name, first letter capitalized)
const getLoggedInUserName = () => {
  try {
    const userObj = JSON.parse(localStorage.getItem("user") || "{}");
    // Prefer the actual user id (e.g. "maithili") for Received By.
    if (userObj?.userId) return userObj.userId;
    if (userObj?.firstName) {
      return userObj.firstName.charAt(0).toUpperCase() + userObj.firstName.slice(1).toLowerCase();
    }
    if (userObj?.email) return getDisplayNameFromEmail(userObj.email);
    if (userObj?.name) return getDisplayNameFromEmail(userObj.name);
  } catch (e) {
    console.error("Error parsing logged-in user:", e);
  }
  const storedUserId = localStorage.getItem("userId");
  if (storedUserId && storedUserId !== "undefined" && storedUserId !== "null") return storedUserId;
  const storedName = localStorage.getItem("userName");
  if (storedName && storedName !== "undefined" && storedName !== "null") {
    return getDisplayNameFromEmail(storedName);
  }
  return "";
};

const GoodsReceiptView = () => {
  const { grnNo } = useParams();
  const [searchParams] = useSearchParams();
  const [grnData, setGrnData] = useState([]);
  const [supplierData, setSupplierData] = useState(null);
  const [statusMsg, setStatusMsg] = useState("Loading...");

  // Refs so PDF function always reads latest data (no stale closure)
  const grnRef      = useRef([]);
  const supplierRef = useRef(null);
  const didTrigger  = useRef(false);   // prevent double-trigger

  // "Received By" = the logged-in user's "First Last", fetched from the
  // backend (localStorage "user" often has no firstName/lastName).
  const receivedByRef = useRef("");
  const [receivedByName, setReceivedByName] = useState("");
  useEffect(() => {
    fetch(`${PURCHASE_API}/goods-receipt/current-user`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => {
        if (d?.name) {
          receivedByRef.current = d.name;
          setReceivedByName(d.name);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => { loadGRN(); }, [grnNo]);

  const loadGRN = async () => {
    try {
      const res  = await fetch(`${PURCHASE_API}/goods-receipt/view/by-grn/${grnNo}`, { headers: authHeaders() });
      const data = await res.json();

      if (res.status === 401) {
        setStatusMsg("Session expired or not signed in — please log in again");
        return;
      }

      const rows = Array.isArray(data) ? data : [];

      if (!rows.length) {
        setStatusMsg("Goods Receipt not found");
        return;
      }

      grnRef.current = rows;
      setGrnData(rows);
      if (rows.length > 0) await loadSupplier(rows[0].vendor_name);
      setStatusMsg("ready");

      // Trigger action AFTER data is in refs
      if (!didTrigger.current) {
        didTrigger.current = true;
        const action = searchParams.get("action");
        if (action === "download") buildAndSavePDF(rows, supplierRef.current);
        else if (action === "print") setTimeout(() => window.print(), 400);
      }
    } catch (err) {
      console.error("GRN load error:", err);
      setStatusMsg("Error: " + err.message);
    }
  };

  const loadSupplier = async (vendorName) => {
    try {
      const res    = await fetch(`${MASTER_API}/business-partner/all`, { headers: authHeaders() });
      const result = await res.json();
      const s      = (result.data || []).find(x => x.business_partner_name === vendorName) || null;
      supplierRef.current = s;
      setSupplierData(s);
    } catch (_) {}
  };

  // ── Convert image src → base64 safely ──────────────────────────────────────
  const toBase64 = (src) =>
    new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const c = document.createElement("canvas");
          c.width  = img.naturalWidth  || 400;
          c.height = img.naturalHeight || 120;
          c.getContext("2d").drawImage(img, 0, 0);
          resolve(c.toDataURL("image/png"));
        } catch { resolve(null); }
      };
      img.onerror = () => resolve(null);
      // cache-bust to avoid opaque response
      img.src = src.startsWith("data:") ? src : src + "?cb=" + Date.now();
    });

  // ── Core PDF builder ────────────────────────────────────────────────────────
  const buildAndSavePDF = async (rows, supplier) => {
    if (!rows || rows.length === 0) return;

    const pdf  = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const PW   = 210, ML = 12, MR = 12, CW = PW - ML - MR;
    let y = 12;

    const guard = (need = 10) => { if (y + need > 282) { pdf.addPage(); y = 12; } };
    const hline = (x1,y1,x2,y2) => { pdf.setDrawColor(0); pdf.line(x1,y1,x2,y2); };
    const box   = (x,by,w,h)    => { pdf.setDrawColor(0); pdf.rect(x,by,w,h); };

    // Logo
    const logoB64 = await toBase64(sbeeLogo);
    if (logoB64) {
      const lw = 48;
      pdf.addImage(logoB64, "PNG", (PW-lw)/2, y, lw, 14);
      y += 18;
    } else {
      pdf.setFontSize(18); pdf.setFont("helvetica","bold");
      pdf.text("SBEE", PW/2, y+8, {align:"center"});
      y += 14;
    }

    // Title
    pdf.setFontSize(15); pdf.setFont("helvetica","bold");
    pdf.text("GOODS RECEIPT NOTE", PW/2, y, {align:"center"});
    y += 9;

    // Info block
    const recvDate = rows[0]?.received_date
      ? new Date(rows[0].received_date).toLocaleDateString() : "";
    const poNo = rows[0]?.purchase_order_id ?? rows[0]?.po_no ?? "";
    const poDate = rows[0]?.po_date || rows[0]?.po_created_at
      ? new Date(rows[0].po_date || rows[0].po_created_at).toLocaleDateString() : "";
    const invoiceDate = rows[0]?.created_at
      ? new Date(rows[0].created_at).toLocaleDateString() : "";
    pdf.setFontSize(10);

    pdf.setFont("helvetica","bold");   pdf.text("GRN NO :",       ML, y);
    pdf.setFont("helvetica","normal"); pdf.text(String(rows[0]?.grn_number  ?? ""), ML+22, y);
    pdf.setFont("helvetica","bold");   pdf.text("DATE :",          ML, y+6);
    pdf.setFont("helvetica","normal"); pdf.text(recvDate,          ML+22, y+6);
    pdf.setFont("helvetica","bold");   pdf.text("PO NO :",         ML, y+12);
    pdf.setFont("helvetica","normal"); pdf.text(String(poNo),      ML+22, y+12);
    pdf.setFont("helvetica","bold");   pdf.text("PO DATE :",       ML, y+18);
    pdf.setFont("helvetica","normal"); pdf.text(poDate,            ML+22, y+18);

    const rx = PW/2+5;
    pdf.setFont("helvetica","bold");   pdf.text("SUPPLIER NAME :", rx, y);
    pdf.setFont("helvetica","normal"); pdf.text(String(rows[0]?.vendor_name ?? ""), rx+36, y);
    pdf.setFont("helvetica","bold");   pdf.text("INVOICE NO :", rx, y+6);
    pdf.setFont("helvetica","normal"); pdf.text(String(rows[0]?.invoice_no ?? ""), rx+28, y+6);
    pdf.setFont("helvetica","bold");   pdf.text("INVOICE DATE :", rx, y+12);
    pdf.setFont("helvetica","normal"); pdf.text(invoiceDate,       rx+28, y+12);
    y += 24;

    // Table
    const cols = [
  { label: "Sl No", w: 10 },
  { label: "Product Code", w: 25 },
  { label: "Material Description", w: 45 },
  { label: "UOM", w: 15 },
  { label: "Qty Ordered", w: 18 },
  { label: "Qty Received", w: 18 },
  { label: "Accepted", w: 18 },
  { label: "Rejected", w: 15 },
  { label: "Remarks", w: CW - 10 - 25 - 45 - 15 - 18 - 18 - 18 - 15 },
];
    const HH=9, RH=8;

    pdf.setFillColor(230,230,230);
    pdf.rect(ML, y, CW, HH, "FD");
    pdf.setFontSize(8.5); pdf.setFont("helvetica","bold");
    let cx=ML;
    cols.forEach(col=>{
      if(cx>ML) hline(cx,y,cx,y+HH);
      pdf.text(col.label, cx+col.w/2, y+5.8, {align:"center"});
      cx+=col.w;
    });
    y+=HH;

    pdf.setFont("helvetica","normal"); pdf.setFontSize(9);
    rows.forEach((item,idx)=>{
      guard(RH); box(ML,y,CW,RH);
      const normStatus = String(item.status || "").toLowerCase();
      const isRejected = normStatus === "rejected";
      const isAccepted = normStatus === "in stock"; // set only after Quality passes the batch
      const acceptedQty = isAccepted ? item.received_qty : 0;
      const rejectedQty = isRejected ? item.received_qty : 0;
      const cells=[
  {val:idx+1, align:"center"},
  {val:item.product_code, align:"center"},
  {val:item.description, align:"left"},
  {val:item.uom, align:"center"},
  {val:item.requirement_qty, align:"center"},
  {val:item.received_qty, align:"center"},
  {val:acceptedQty, align:"center"},
  {val:rejectedQty, align:"center"},
  {val:item.status, align:"center"},
];
      cx=ML;
      cols.forEach((col,ci)=>{
        if(cx>ML) hline(cx,y,cx,y+RH);
        const c=cells[ci];
        const v=String(c.val??'');
        const tx=c.align==="center"?cx+col.w/2:cx+2;
        pdf.text((pdf.splitTextToSize(v,col.w-3)[0]??''), tx, y+5.2, {align:c.align});
        cx+=col.w;
      });
      y+=RH;
    });

    // Footer — Prepared By / Received By / Checked By
    y+=4; guard(30);
    const fbH = 26;
    box(ML,y,CW,fbH);
    const colW = CW/3;
    hline(ML+colW, y, ML+colW, y+fbH);
    hline(ML+colW*2, y, ML+colW*2, y+fbH);

    const preparedByName = getDisplayNameFromEmail(
      rows[0]?.created_by ?? rows[0]?.createdBy ?? rows[0]?.creted_by ?? rows[0]?.cretedBy ?? ""
    );
    const receivedByName = receivedByRef.current || getLoggedInUserName();

    pdf.setFontSize(10); pdf.setFont("helvetica","bold");
    pdf.text("Prepared By",  ML+colW*0.5,      y+7, {align:"center"});
    pdf.text("Received By",  ML+colW*1.5,      y+7, {align:"center"});
    pdf.text("Checked By",   ML+colW*2.5,      y+7, {align:"center"});
    pdf.setFont("helvetica","normal"); pdf.setFontSize(8);
    pdf.text(preparedByName, ML+colW*0.5, y+13, {align:"center"});
    pdf.text(receivedByName, ML+colW*1.5, y+13, {align:"center"});
    y += fbH;

    // Bottom meta — Format No. (left) / Revision Date (right)
    y += 8; guard(6);
    pdf.setFontSize(9); pdf.setFont("helvetica","bold");
    pdf.text(`Format No. G1001`, ML, y);
    pdf.text(`Revision Date : 01/01/2026`, PW-MR, y, {align:"right"});

    pdf.save(`${grnNo}_GoodsReceipt.pdf`);
  };

  // Styles for the on-screen view
  const th  = {border:"1px solid black",padding:"8px",textAlign:"center",fontWeight:"bold",fontSize:"14px"};
  const td  = {border:"1px solid black",padding:"8px",textAlign:"center",fontSize:"14px"};
  const tdL = {border:"1px solid black",padding:"8px",textAlign:"left",fontSize:"14px"};

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #grn-container, #grn-container * { visibility: visible; }
          #grn-container { position: absolute; left: 0; top: 0; width: 100%; }
          @page { size: A4; margin: 10mm; }
        }
      `}</style>

      <div
        id="grn-container"
        style={{width:"1000px",margin:"20px auto",border:"1px solid black",
                padding:"15px",fontFamily:"Calibri,Arial,sans-serif",background:"#fff"}}
      >
        {grnData.length === 0 ? (
          <div style={{padding:20,color:"#64748b"}}>{statusMsg}</div>
        ) : (
          <>
            <div style={{textAlign:"center",marginBottom:"15px"}}>
              <img src={sbeeLogo} alt="SBEE" style={{width:"220px",height:"auto"}} />
            </div>

            <h3 style={{textAlign:"center",marginTop:"5px",marginBottom:"20px",fontSize:"24px",fontWeight:"bold"}}>
              GOODS RECEIPT NOTE
            </h3>

            <div style={{display:"flex",justifyContent:"space-between",marginBottom:"20px"}}>
              <div>
                <p><b>GRN NO :</b> {grnData[0]?.grn_number}</p>
                <p><b>DATE :</b> {new Date(grnData[0]?.received_date).toLocaleDateString()}</p>
                <p><b>PO NO :</b> {grnData[0]?.purchase_order_id || grnData[0]?.po_no || ""}</p>
                <p><b>PO DATE :</b> {(grnData[0]?.po_date || grnData[0]?.po_created_at) ? new Date(grnData[0].po_date || grnData[0].po_created_at).toLocaleDateString() : ""}</p>
              </div>
              <div>
                <p><b>SUPPLIER NAME :</b> {grnData[0]?.vendor_name}</p>
                <p><b>INVOICE NO :</b> {grnData[0]?.invoice_no || ""}</p>
                <p><b>INVOICE DATE :</b> {grnData[0]?.created_at ? new Date(grnData[0].created_at).toLocaleDateString() : ""}</p>
              </div>
            </div>
<table style={{width:"100%",borderCollapse:"collapse"}}>
  <thead>
    <tr>
      <th style={th}>Sl No</th>
      <th style={th}>Product Code</th>
      <th style={th}>Material Description</th>
      <th style={th}>UOM</th>
      <th style={th}>Qty Ordered</th>
      <th style={th}>Qty Received</th>
      <th style={th}>Accepted</th>
      <th style={th}>Rejected</th>
      <th style={th}>Remarks</th>
    </tr>
  </thead>

  <tbody>
    {grnData.map((item,index)=>{
      const normStatus = String(item.status || "").toLowerCase();
      const isRejected = normStatus === "rejected";
      const isAccepted = normStatus === "in stock"; // set only after Quality passes the batch
      const acceptedQty = isAccepted ? item.received_qty : 0;
      const rejectedQty = isRejected ? item.received_qty : 0;
      return (
      <tr key={index}>
        <td style={td}>{index+1}</td>

        <td style={td}>
          {item.product_code || item.productCode || "-"}
        </td>

        <td style={tdL}>
          {item.description}
        </td>

        <td style={td}>
          {item.uom || item.base_unit || "-"}
        </td>

        <td style={td}>
          {item.requirement_qty}
        </td>

        <td style={td}>
          {item.received_qty}
        </td>

        <td style={td}>
          {acceptedQty}
        </td>

        <td style={td}>
          {rejectedQty}
        </td>

        <td style={td}>
          {item.status}
        </td>
      </tr>
      );
    })}
  </tbody>
</table>

            <div style={{marginTop:"20px",border:"1px solid black",padding:"10px",minHeight:"90px"}}>
              <div style={{display:"flex",justifyContent:"space-between"}}>
 <div style={{textAlign:"center",flex:1}}>
    <b>Prepared By</b>
    <p style={{marginTop:"4px", fontSize:"13px"}}>
  {getDisplayNameFromEmail(
    grnData[0]?.created_by || grnData[0]?.createdBy || grnData[0]?.creted_by || grnData[0]?.cretedBy || ""
  )}
</p>
  </div>
                  <div style={{textAlign:"center",flex:1}}>
                  <b>Received By</b>
                  <p style={{marginTop:"4px", fontSize:"13px"}}>{receivedByName || getLoggedInUserName()}</p>
                </div>
                <div style={{textAlign:"center",flex:1}}><b>Checked By</b></div>
              </div>
            </div>

            <div style={{display:"flex",justifyContent:"space-between",marginTop:"15px",fontSize:"13px",fontWeight:"bold"}}>
              <span>Format No. G1001</span>
              <span>Revision Date : 01/01/2026</span>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default GoodsReceiptView;